import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private readonly prefix = 'asf24';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
  }

  get<T>(key: string, defaultValue: T | null = null): T | null {
    if (!this.isAvailable()) {
      return defaultValue;
    }

    const storageKey = this.buildKey(key);
    const value = localStorage.getItem(storageKey);

    if (value === null) {
      return defaultValue;
    }

    try {
      return JSON.parse(value) as T;
    } catch (e) {
      localStorage.removeItem(storageKey);
      return defaultValue;
    }
  }

  set<T>(key: string, value: T): void {
    if (!this.isAvailable()) {
      return;
    }

    try {
      localStorage.setItem(this.buildKey(key), JSON.stringify(value));
    } catch (e) {
      console.error(
        `Nie udało się zapisać wartości w localStorage dla klucza: ${key}`,
        e
      );
    }
  }

  has(key: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }

    return localStorage.getItem(this.buildKey(key)) !== null;
  }

  remove(key: string): void {
    if (!this.isAvailable()) {
      return;
    }

    localStorage.removeItem(this.buildKey(key));
  }

  clearNamespace(namespace: string): void {
    if (!this.isAvailable()) {
      return;
    }

    const prefix = this.buildKey(namespace);
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.indexOf(prefix) === 0) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key: string) => {
      localStorage.removeItem(key);
    });
  }

  private buildKey(key: string): string {
    return `${this.prefix}.${key}`;
  }

  private isAvailable(): boolean {
    return isPlatformBrowser(this.platformId) && typeof localStorage !== 'undefined';
  }
}
